/**
 * Cache wrapper for mission-control logs. The leaf class `CachedMissionControlLogs` owns a
 * `RangeCache<MissionControlLog>` keyed by operator username; widening a previously-loaded
 * window for the same user fetches only the gap. Calls into `API.MissionControl.getLogs` for
 * the underlying HTTP fetch.
 */

import type { MissionControlLog } from "@domain/MissionControl";
import { RangeCache } from "../cache/rangeCache";
import { API } from "../api";

class CachedMissionControlLogs {
	private cache = new RangeCache<MissionControlLog>({
		timestampOf: (l) => new Date(l.timestamp).getTime(),
		// Logs occasionally arrive with a stable `id`; dedupe in case overlapping fetches return
		// the same row twice. Synthetic key when id is missing.
		keyOf: (l) => (l.id != null ? `id:${l.id}` : `${l.userId}::${l.timestamp}::${l.eventType ?? ""}`),
	});

	public getInRange(user: string, start: Date, end: Date): Promise<MissionControlLog[]> {
		return this.cache.query(user, [start.getTime(), end.getTime()], (key, gap) =>
			API.MissionControl.getLogs(key, new Date(gap[0]).toISOString(), new Date(gap[1]).toISOString()),
		);
	}

	public clear(): void {
		this.cache.clear();
	}
}

export class CachedMissionControlAPI {
	public logs = new CachedMissionControlLogs();
}
